/**
 * TYPING-RETURN-002 — "Returned from Typing" Correction Required banner.
 *
 * Shown on the order view when Typing returned the file (status
 * pending_correction + typing_status returned). Deliberately distinct from
 * the QC ProcessorFeedbackBanner: different source label, endpoint and
 * acknowledgment field, so QC and Typing feedback never mix.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Keyboard, RotateCcw, CheckCircle2 } from 'lucide-react';
import { formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TypingReturnBanner = ({ orderId, currentUser, onAcknowledged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(false);
  const [error, setError] = useState('');

  const fetchFeedback = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${orderId}/typing/feedback`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
      else setData(null);
    } catch (e) {
      setError('Network error loading Typing feedback');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  if (loading || !data || !data.feedback) return null;

  const fb = data.feedback;
  const returnedAt = fb.returned_at ? formatDateTimeLocal(fb.returned_at) : null;
  const isAcked = !!data.acknowledged_at;

  const handleAcknowledge = async () => {
    setAcking(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${orderId}/typing/acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        await fetchFeedback();
        if (onAcknowledged) onAcknowledged({
          typing_return_acknowledged_at: body?.typing_return_acknowledged_at || new Date().toISOString(),
        });
      } else {
        setError(body.detail || 'Failed to acknowledge');
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setAcking(false);
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-300 rounded-xl p-4 mb-4" data-testid="typing-return-banner">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5"><Keyboard className="w-5 h-5 text-blue-700" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-sm font-semibold text-blue-900 inline-flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-blue-700" /> Returned from Typing — Correction Required
            </h3>
            {isAcked && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="typing-return-acked-chip">
                <CheckCircle2 className="w-3.5 h-3.5" /> Acknowledged
              </span>
            )}
          </div>

          <div className="text-xs text-blue-700 mt-1">
            Returned by <span className="font-medium">{fb.returned_by_name || 'Typing Processor'}</span>
            {returnedAt && <> · {returnedAt}</>}
          </div>

          {fb.reason && (
            <div className="mt-3 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-0.5">Typing Return Reason</div>
              <p className="text-sm text-gray-800" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} data-testid="typing-return-reason-text">{fb.reason}</p>
            </div>
          )}

          <div className="mt-4 flex items-center gap-3">
            {!isAcked ? (
              <button type="button" onClick={handleAcknowledge} disabled={acking}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50"
                data-testid="typing-return-acknowledge-btn">
                {acking ? 'Acknowledging…' : 'Acknowledge & Open for Edit'}
              </button>
            ) : (
              <span className="text-xs text-gray-600">
                You can now correct the Fulfillment data and click Complete — the order will automatically return to Typing.
              </span>
            )}
          </div>

          {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default TypingReturnBanner;
